// Eén documentmodel voor scherm, publieke deelpagina en afdruk, gebaseerd op de zeven vaste
// programmaonderdelen van het referentiedocument (connectles februari 2025):
//   1 Welkom · 2 Openingsgebed · 3 Voorstellen · 4 Getuigenissen/dankzegging ·
//   5 Korte studie / overdenking vanuit het Woord · 6 Gebedsnoden · 7 Afsluitend gebed
// Alle zeven onderdelen staan er altijd, vast genummerd 1 t/m 7 (ook als een onderdeel nog leeg is),
// zodat de nummering van elke connectles gelijk is aan het Word-voorbeeld.
// Onderdeel 5 bestaat uit blokken (thema, subkop, tekst, lijst, Bijbelgedeelte, vraag); een latere blokeditor
// vervangt alleen de bron van die blokken, niet de weergave.

export const STUDY_LABEL = 'Korte studie / overdenking vanuit het Woord';

const clean = (value) => (typeof value === 'string' ? value.trim() : '');
const textBlocks = (value) => (clean(value) ? [{ type: 'text', text: clean(value) }] : []);

// Een lijst wordt alleen herkend als álle niet-lege regels een duidelijke lijstmarkering hebben (1. / 1) / - / • / *);
// anders blijft de tekst ongewijzigd een gewone alinea. Er wordt niets herschreven of samengevoegd.
const MARKER = /^\s*(\d+[.)]|[-•*–])\s+/;
const listOrText = (value) => {
  const text = clean(value);
  if (!text) return [];
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length && lines.every((line) => MARKER.test(line))) {
    return [{ type: 'list', ordered: /^\d/.test(lines[0]), items: lines.map((line) => line.replace(MARKER, '')) }];
  }
  return [{ type: 'text', text }];
};
const withHeading = (label, blocks) => (blocks.length ? [{ type: 'subheading', text: label }, ...blocks] : []);

// Onderdeel 5 in vaste volgorde, elk subonderdeel alleen als er inhoud is:
// thema · inleiding · bijbelgedeelten · overdenking · kernpunten · praktische toepassing · gespreksvragen.
// Een Bijbelgedeelte bevat in het lesmodel alleen een verwijzing en een toelichting (geen brontekst);
// de toelichting wordt daarom nooit als letterlijk citaat getoond.
const studyBlocks = (lesson) => {
  const passages = (lesson.bible_passages || [])
    .filter((passage) => clean(passage.reference))
    .map((passage) => ({ type: 'scripture', reference: clean(passage.reference), text: clean(passage.explanation) }));
  const questions = (lesson.questions || []).filter((question) => clean(question.text))
    .map((question, index) => ({ type: 'question', number: index + 1, text: clean(question.text) }));
  return [
    ...(clean(lesson.theme) ? [{ type: 'theme', text: clean(lesson.theme) }] : []),
    ...withHeading('Inleiding', textBlocks(lesson.introduction)),
    ...withHeading('Bijbelgedeelten', passages),
    ...withHeading('Overdenking', textBlocks(lesson.study_synthesis)),
    ...withHeading('Kernpunten', listOrText(lesson.key_points)),
    ...withHeading('Praktische toepassing', listOrText(lesson.practical_application)),
    ...withHeading('Gespreksvragen', questions),
  ];
};

export const buildLessonDocument = (lesson) => {
  const sections = [
    { key: 'welcome', label: 'Welkom', blocks: textBlocks(lesson.opening_welcome) },
    { key: 'opening_prayer', label: 'Openingsgebed', blocks: textBlocks(lesson.opening_prayer) },
    { key: 'introductions', label: 'Voorstellen', blocks: textBlocks(lesson.opening_introductions) },
    { key: 'testimonies', label: 'Getuigenissen/dankzegging', blocks: textBlocks(lesson.opening_testimonies) },
    { key: 'study', label: STUDY_LABEL, blocks: studyBlocks(lesson) },
    { key: 'prayer_needs', label: 'Gebedsnoden', blocks: textBlocks(lesson.prayer_points) },
    { key: 'closing_prayer', label: 'Afsluitend gebed', blocks: textBlocks(lesson.closing) },
  ].map((section, index) => ({ ...section, number: index + 1, empty: section.blocks.length === 0 }));

  return {
    title: clean(lesson.title) || 'Connectles',
    series: clean(lesson.series),
    weekNumber: lesson.week_number || null,
    lessonDate: lesson.lesson_date || null,
    sections,
    agenda: textBlocks(lesson.agenda_note),
  };
};

export const documentYear = (lessonDate, fallback = new Date().getFullYear()) => {
  const year = lessonDate ? Number(String(lessonDate).slice(0, 4)) : NaN;
  return Number.isFinite(year) && year > 1900 ? year : fallback;
};
